(() => {
  const searchDetails = () => document.querySelector('.shoev-header__icons .header__search details');
  const cartDrawer = () => document.querySelector('cart-drawer');
  const mobileMenuToggle = () => document.querySelector('.shoev-header__menu-toggle[aria-expanded="true"]');
  const lockState = {
    active: false,
    scrollY: 0,
    lenis: null,
    lenisWasStopped: true,
  };

  function currentScrollPosition() {
    const menuOffset = getComputedStyle(document.body)
      .getPropertyValue('--shoev-drawer-scroll-offset')
      .trim();
    const parsedOffset = Number.parseFloat(menuOffset);
    if (document.documentElement.classList.contains('shoev-drawer-open') && Number.isFinite(parsedOffset)) {
      return Math.abs(parsedOffset);
    }
    return window.scrollY || window.pageYOffset || 0;
  }

  function isSearchOpen() {
    return Boolean(searchDetails()?.hasAttribute('open'));
  }

  function isCartOpen() {
    return Boolean(cartDrawer()?.classList.contains('active'));
  }

  function lockPageScroll() {
    if (lockState.active) {
      if (lockState.lenis && !lockState.lenis.isStopped && typeof lockState.lenis.stop === 'function') {
        lockState.lenis.stop();
      }
      return;
    }
    lockState.scrollY = currentScrollPosition();
    lockState.lenis = window.shoevLenis || null;
    lockState.lenisWasStopped = !lockState.lenis || Boolean(lockState.lenis.isStopped);

    if (lockState.lenis && !lockState.lenisWasStopped && typeof lockState.lenis.stop === 'function') {
      lockState.lenis.stop();
    }

    document.body.style.setProperty('--shoev-commerce-scroll-offset', `${-lockState.scrollY}px`);
    document.documentElement.classList.add('shoev-commerce-locked');
    document.body.classList.add('shoev-commerce-locked');
    lockState.active = true;
  }

  function unlockPageScroll() {
    if (!lockState.active || isSearchOpen() || isCartOpen()) return;

    document.documentElement.classList.remove('shoev-commerce-locked');
    document.body.classList.remove('shoev-commerce-locked');
    document.body.style.removeProperty('--shoev-commerce-scroll-offset');

    if (document.documentElement.classList.contains('shoev-drawer-open')) {
      lockState.active = false;
      lockState.lenis = null;
      lockState.lenisWasStopped = true;
      return;
    }

    const previousBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockState.scrollY);
    document.documentElement.style.scrollBehavior = previousBehavior;

    if (lockState.lenis && !lockState.lenisWasStopped && typeof lockState.lenis.start === 'function') {
      lockState.lenis.start();
    }

    lockState.active = false;
    lockState.lenis = null;
    lockState.lenisWasStopped = true;
  }

  function syncScrollLock() {
    if (isSearchOpen() || isCartOpen()) {
      lockPageScroll();
      return;
    }
    window.requestAnimationFrame(unlockPageScroll);
  }

  function closeMobileMenu() {
    const toggle = mobileMenuToggle();
    if (toggle) toggle.click();
  }

  function closeSearch() {
    const details = searchDetails();
    if (!details || !details.hasAttribute('open')) return;
    const modal = details.closest('details-modal');
    if (modal && typeof modal.close === 'function') modal.close(false);
    else details.removeAttribute('open');
    document.documentElement.classList.remove('shoev-search-open');
    syncScrollLock();
  }

  function syncCartCount() {
    const source = document.getElementById('cart-icon-bubble');
    const target = document.querySelector('[data-shoev-cart-count]');
    const trigger = document.querySelector('[data-shoev-cart-trigger]');
    if (!source || !target || !trigger) return;

    const countNode = source.querySelector('.cart-count-bubble [aria-hidden="true"]');
    const count = countNode ? countNode.textContent.trim() : '0';
    target.textContent = count;
    target.classList.toggle('is-hidden', count === '0' || count === '');
    trigger.setAttribute('aria-label', `Cart (${count || '0'})`);
  }

  function syncCartPageCount() {
    const countRoot = document.querySelector('[data-shoev-cart-page-count]');
    const itemsRoot = document.getElementById('main-cart-items');
    if (!countRoot || !itemsRoot) return;

    const count = Array.from(itemsRoot.querySelectorAll('.quantity__input[name="updates[]"]')).reduce(
      (total, input) => total + (Number.parseInt(input.value, 10) || 0),
      0
    );
    const value = countRoot.querySelector('[data-shoev-cart-page-count-value]');
    const label = countRoot.querySelector('[data-shoev-cart-page-count-label]');
    if (value) value.textContent = String(count);
    if (label) label.textContent = count === 1 ? 'item' : 'items';
  }

  function enableMobileDrawerDismiss(drawer) {
    const handle = drawer.querySelector('.shoev-cart-drawer__handle');
    const panel = drawer.querySelector('.drawer__inner');
    if (!handle || !panel || handle.dataset.shoevSwipeReady === 'true') return;

    handle.dataset.shoevSwipeReady = 'true';
    let startY = 0;
    let startTime = 0;
    let distance = 0;

    const finish = (event) => {
      if (!startTime) return;
      if (event?.pointerId != null && handle.hasPointerCapture?.(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }

      const elapsed = Math.max(performance.now() - startTime, 1);
      const velocity = distance / elapsed;
      const shouldClose = distance > Math.max(72, panel.offsetHeight * 0.12) || velocity > 0.55;

      panel.style.removeProperty('transition');
      if (shouldClose && typeof drawer.close === 'function') {
        drawer.close();
        window.setTimeout(() => panel.style.removeProperty('transform'), 260);
      } else {
        panel.style.removeProperty('transform');
      }

      startTime = 0;
      distance = 0;
    };

    handle.addEventListener('pointerdown', (event) => {
      if (!drawer.classList.contains('active') || window.matchMedia('(min-width: 990px)').matches) return;
      startY = event.clientY;
      startTime = performance.now();
      distance = 0;
      handle.setPointerCapture?.(event.pointerId);
      panel.style.transition = 'none';
    });

    handle.addEventListener('pointermove', (event) => {
      if (!startTime) return;
      distance = Math.max(0, event.clientY - startY);
      panel.style.transform = `translateY(${distance}px)`;
    });

    handle.addEventListener('pointerup', finish);
    handle.addEventListener('pointercancel', finish);
  }

  function prepareOverlayScrollContainers(drawer) {
    document.querySelectorAll(
      '.shoev-header__icons .predictive-search--header, .shoev-header__icons .search-modal__content'
    ).forEach((element) => element.setAttribute('data-lenis-prevent', ''));

    if (!drawer) return;
    drawer.querySelectorAll('cart-drawer-items, .drawer__contents').forEach((element) => {
      element.setAttribute('data-lenis-prevent', '');
    });
  }

  document.addEventListener('click', (event) => {
    const cartTrigger = event.target.closest('[data-shoev-cart-trigger]');
    if (cartTrigger) {
      const drawer = cartDrawer();
      if (!drawer || typeof drawer.open !== 'function') return;
      event.preventDefault();
      closeSearch();
      closeMobileMenu();
      drawer.open(cartTrigger);
      return;
    }

    if (event.target.closest('.shoev-header__icons .header__search summary')) {
      const drawer = cartDrawer();
      if (drawer && drawer.classList.contains('active')) drawer.close();
      closeMobileMenu();
    }
  }, true);

  function initialize() {
    syncCartCount();
    syncCartPageCount();

    const source = document.getElementById('cart-icon-bubble');
    if (source) {
      new MutationObserver(syncCartCount).observe(source, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const cartPageItems = document.getElementById('main-cart-items');
    if (cartPageItems) {
      new MutationObserver(syncCartPageCount).observe(cartPageItems, {
        childList: true,
        subtree: true,
      });
    }

    const details = searchDetails();
    if (details) {
      new MutationObserver(() => {
        const isOpen = details.hasAttribute('open');
        document.documentElement.classList.toggle('shoev-search-open', isOpen);
        if (isOpen) {
          const drawer = cartDrawer();
          if (drawer && drawer.classList.contains('active')) drawer.close();
          closeMobileMenu();
        }
        syncScrollLock();
      }).observe(details, { attributes: true, attributeFilter: ['open'] });
    }

    const drawer = cartDrawer();
    if (drawer) {
      enableMobileDrawerDismiss(drawer);
      prepareOverlayScrollContainers(drawer);
      new MutationObserver((mutations) => {
        const classChanged = mutations.some(
          (mutation) => mutation.type === 'attributes' && mutation.attributeName === 'class'
        );
        const contentsChanged = mutations.some((mutation) => mutation.type === 'childList');

        if (contentsChanged) {
          enableMobileDrawerDismiss(drawer);
          prepareOverlayScrollContainers(drawer);
        }

        if (classChanged) {
          const isOpen = drawer.classList.contains('active');
          document.documentElement.classList.toggle('shoev-cart-open', isOpen);
          if (isOpen) {
            closeSearch();
            closeMobileMenu();
          }
          syncScrollLock();
        }
      }).observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });
    }

    prepareOverlayScrollContainers(drawer);
    syncScrollLock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
